import React, { useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Card, Chip, Avatar, Grid,
  IconButton, MenuItem, InputAdornment, Tooltip, Button, TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search, Download, CalendarToday, Apartment, Refresh, Clear,
  Business, Home, Villa, CheckCircle, Cancel, Schedule,
  HourglassEmpty, MoreVert, TrendingUp
} from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import { vendorApi } from '../../api/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';

dayjs.extend(isToday);
dayjs.extend(isYesterday);

/* ── Config ─────────────────────────────────────────────── */
const STATUS_CONFIG = {
  Scheduled: { color: '#3B82F6', bg: '#EFF6FF', label: 'Scheduled' },
  completed:  { color: '#10B981', bg: '#ECFDF5', label: 'Completed' },
  cancelled:  { color: '#EF4444', bg: '#FEF2F2', label: 'Cancelled' },
  confirmed:  { color: '#6366F1', bg: '#EEF2FF', label: 'Confirmed' },
  pending:    { color: '#F59E0B', bg: '#FFFBEB', label: 'Pending'   },
};

const PROPERTY_TYPE_CONFIG = {
  apartment: { color: '#6366F1', bg: '#EEF2FF', label: 'Apartment',      icon: <Apartment /> },
  project:   { color: '#10B981', bg: '#ECFDF5', label: 'Project',         icon: <Business />  },
  property:  { color: '#F59E0B', bg: '#FFFBEB', label: 'Property',        icon: <Home />      },
  villa:     { color: '#EC4899', bg: '#FDF2F8', label: 'Individual Villa', icon: <Villa />     },
};

const getPropertyTypeInfo = (row) => {
  const type = row.PropertyType?.toLowerCase() || '';
  if (type.includes('apartment')) return PROPERTY_TYPE_CONFIG.apartment;
  if (type.includes('project'))   return PROPERTY_TYPE_CONFIG.project;
  if (type.includes('villa'))     return PROPERTY_TYPE_CONFIG.villa;
  if (row.ApartmentName)          return PROPERTY_TYPE_CONFIG.apartment;
  if (row.project_name)           return PROPERTY_TYPE_CONFIG.project;
  return PROPERTY_TYPE_CONFIG.property;
};

const getPropertyDisplayName = (row) => {
  if (row.ApartmentName && row.ApartmentName !== 'N/A') return row.ApartmentName;
  if (row.project_name  && row.project_name  !== 'N/A') return row.project_name;
  if (row.propertyName  && row.propertyName  !== 'N/A') return row.propertyName;
  return 'Property Name Not Available';
};

const getDateLabel = (dateStr) => {
  const d = dayjs(dateStr);
  if (d.isToday())     return 'Today';
  if (d.isYesterday()) return 'Yesterday';
  return d.format('DD MMM YYYY');
};

/* ── Sub-components ──────────────────────────────────────── */
const StatCard = ({ label, value, color, icon, sub }) => (
  <Card elevation={0} sx={{
    p: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#fff',
    transition: 'box-shadow .2s', '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,.08)' }
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography sx={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600, letterSpacing: '.04em', mb: 0.5 }}>
          {label.toUpperCase()}
        </Typography>
        <Typography sx={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: 11, color: '#9CA3AF', mt: 0.5 }}>{sub}</Typography>}
      </Box>
      <Avatar sx={{ bgcolor: color + '18', width: 44, height: 44 }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
      </Avatar>
    </Box>
  </Card>
);

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: '#6B7280', bg: '#F3F4F6', label: status };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.5, py: 0.4, borderRadius: 20, bgcolor: cfg.bg, color: cfg.color,
      fontSize: 12, fontWeight: 600, border: `1px solid ${cfg.color}30`
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.color }} />
      {cfg.label}
    </Box>
  );
};

/* ── Main ────────────────────────────────────────────────── */
const SiteVisit = () => {
  const [siteVisits,         setSiteVisits]         = useState([]);
  const [filteredVisits,     setFilteredVisits]     = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [filterDate,         setFilterDate]         = useState('');
  const [searchKeyword,      setSearchKeyword]      = useState('');
  const [statusFilter,       setStatusFilter]       = useState('all');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');
  const [anchorEl,           setAnchorEl]           = useState(null);
  const [selectedRow,        setSelectedRow]        = useState(null);
  const [pageSize,           setPageSize]           = useState(50);

  useEffect(() => { fetchSiteVisits(); }, []);

  useEffect(() => {
    let f = siteVisits;
    if (filterDate)                f = f.filter(v => dayjs(v.scheduledDateTime).format('YYYY-MM-DD') === filterDate);
    if (statusFilter !== 'all')    f = f.filter(v => v.status === statusFilter);
    if (propertyTypeFilter !== 'all')
      f = f.filter(v => getPropertyTypeInfo(v).label.toLowerCase() === propertyTypeFilter.toLowerCase());
    if (searchKeyword.trim())
      f = f.filter(v =>
        [v.username, v.propertyName, v.ApartmentName, v.project_name,
         v.mobilenumber?.toString(), v.PropertyType]
          .some(s => s?.toLowerCase().includes(searchKeyword.toLowerCase()))
      );
    setFilteredVisits(f);
  }, [filterDate, searchKeyword, statusFilter, propertyTypeFilter, siteVisits]);

  const fetchSiteVisits = async () => {
    try {
      setLoading(true);
      const res  = await vendorApi.getAllSiteVisits();
      const data = (res.data || []).map(v => ({
        ...v,
        ApartmentName: v.ApartmentName || null,
        propertyName:  v.propertyName  || null,
        project_name:  v.project_name  || null,
        PropertyType:  v.PropertyType  || null,
        status:        v.status        || 'pending',
      }));
      setSiteVisits(data);
      setFilteredVisits(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleActionClick  = (e, row) => { setAnchorEl(e.currentTarget); setSelectedRow(row); };
  const handleCloseMenu    = ()        => { setAnchorEl(null); setSelectedRow(null); };
  const handleStatusChange = (status) => {
    setSiteVisits(prev => prev.map(i => i.id === selectedRow.id ? { ...i, status } : i));
    handleCloseMenu();
  };

  const handleExport = () => {
    const rows = filteredVisits.map(v => ({
      ID: v.id, Username: v.username,
      'Project Name': v.project_name, 'Apartment Name': v.ApartmentName,
      'Property Name': v.propertyName, 'Property Type': v.PropertyType,
      'Mobile Number': v.mobilenumber,
      'Scheduled Date': dayjs(v.scheduledDateTime).format('DD MMM YYYY'),
      'Scheduled Time': dayjs(v.scheduledDateTime).format('hh:mm A'),
      Status: v.status,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SiteVisits');
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }),
      `SiteVisits_${dayjs().format('DD-MM-YYYY')}.xlsx`
    );
  };

  const clearFilters = () => { setFilterDate(''); setSearchKeyword(''); setStatusFilter('all'); setPropertyTypeFilter('all'); };
  const cnt          = (s) => siteVisits.filter(v => v.status === s).length;
  const hasFilters   = filterDate || statusFilter !== 'all' || propertyTypeFilter !== 'all' || searchKeyword;

  /* ── Build date-grouped rows ── */
  const groupedRows = React.useMemo(() => {
    const sorted = [...filteredVisits].sort(
      (a, b) => dayjs(b.scheduledDateTime).valueOf() - dayjs(a.scheduledDateTime).valueOf()
    );

    // Count per date label
    const countMap = {};
    sorted.forEach(v => {
      const lbl = getDateLabel(v.scheduledDateTime);
      countMap[lbl] = (countMap[lbl] || 0) + 1;
    });

    const result = [];
    let lastLabel = null;

    sorted.forEach((visit) => {
      const label = getDateLabel(visit.scheduledDateTime);
      if (label !== lastLabel) {
        result.push({
          id:         `header-${label}`,
          isHeader:   true,
          dateLabel:  label,
          visitCount: countMap[label],
        });
        lastLabel = label;
      }
      result.push({ ...visit, isHeader: false });
    });

    return result;
  }, [filteredVisits]);

  /* ── Columns ── */
  const columns = [
    {
      field: 'sno', headerName: '#', width: 60, headerAlign: 'center', align: 'center',
      renderCell: (p) => {
        if (p.row.isHeader) return null;
        return (
          <Typography sx={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>
            {p.api.getRowIndexRelativeToVisibleRows(p.id)}
          </Typography>
        );
      },
    },
    {
      field: 'userInfo', headerName: 'USER', width: 210,
      renderCell: (p) => {
        // ── Date Header spanning first column ──
        if (p.row.isHeader) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 0.5, borderRadius: 20,
                bgcolor: '#EEF2FF', border: '1px solid #C7D2FE'
              }}>
                <CalendarToday sx={{ fontSize: 13, color: '#6366F1' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6366F1' }}>
                  {p.row.dateLabel}
                </Typography>
              </Box>
              <Box sx={{ px: 1.5, py: 0.2, borderRadius: 10, bgcolor: '#F3F4F6' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#6B7280' }}>
                  {p.row.visitCount} visit{p.row.visitCount !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: '#6366F1', fontSize: 13, fontWeight: 700 }}>
              {p.row.username?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.2 }}>
                {p.row.username || 'N/A'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#6B7280' }}>
                {p.row.mobilenumber || '—'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'propertyInfo', headerName: 'PROPERTY', width: 380,
      renderCell: (p) => {
        if (p.row.isHeader) return null;
        const ti = getPropertyTypeInfo(p.row);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: ti.bg }}>
              {React.cloneElement(ti.icon, { sx: { color: ti.color, fontSize: 16 } })}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.2 }} noWrap>
                {getPropertyDisplayName(p.row)}
              </Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', mt: 0.3,
                px: 1, py: 0.1, borderRadius: 10, bgcolor: ti.bg }}>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: ti.color }}>{ti.label}</Typography>
              </Box>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'scheduledDateTime', headerName: 'VISIT TIME', width: 130,
      renderCell: (p) => {
        if (p.row.isHeader) return null;
        return (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
              {dayjs(p.value).format('hh:mm A')}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
              {dayjs(p.value).format('ddd')}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status', headerName: 'STATUS', width: 130,
      renderCell: (p) => {
        if (p.row.isHeader) return null;
        return <StatusBadge status={p.value} />;
      },
    },
    {
      field: 'actions', headerName: 'ACTIONS', width: 80, sortable: false,
      renderCell: (p) => {
        if (p.row.isHeader) return null;
        return (
          <>
            <Tooltip title="Change Status">
              <IconButton size="small" onClick={(e) => handleActionClick(e, p.row)}
                sx={{ color: '#6B7280', '&:hover': { color: '#6366F1', bgcolor: '#EEF2FF' } }}>
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && selectedRow?.id === p.row.id}
              onClose={handleCloseMenu}
              PaperProps={{ elevation: 3, sx: { borderRadius: 2, minWidth: 160 } }}
            >
              {[
                { key: 'pending',   label: '⏳ Pending'   },
                { key: 'Scheduled', label: '📅 Scheduled' },
                { key: 'completed', label: '✅ Completed' },
                { key: 'cancelled', label: '❌ Cancelled' },
              ].map(opt => (
                <MenuItem key={opt.key} onClick={() => handleStatusChange(opt.key)}
                  sx={{ fontSize: 13, py: 1, '&:hover': { bgcolor: '#F5F3FF' } }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#F9FAFB', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>Site Visit Management</Typography>
          <Typography sx={{ fontSize: 14, color: '#6B7280', mt: 0.5 }}>Track and manage all site visit requests</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchSiteVisits}
              sx={{ border: '1px solid #E5E7EB', borderRadius: 2,
                '&:hover': { borderColor: '#6366F1', color: '#6366F1', bgcolor: '#EEF2FF' } }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button onClick={handleExport} startIcon={<Download />} variant="contained" size="small"
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#6366F1',
              boxShadow: '0 2px 8px rgba(99,102,241,.35)', '&:hover': { bgcolor: '#4F46E5' } }}>
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Visits', value: siteVisits.length, color: '#6366F1', icon: <TrendingUp />,     sub: 'All time' },
          { label: 'Scheduled',    value: cnt('Scheduled'),  color: '#3B82F6', icon: <Schedule />,       sub: 'Upcoming' },
          { label: 'Completed',    value: cnt('completed'),  color: '#10B981', icon: <CheckCircle />,    sub: 'Done'     },
          { label: 'Pending',      value: cnt('pending'),    color: '#F59E0B', icon: <HourglassEmpty />, sub: 'Awaiting' },
          { label: 'Cancelled',    value: cnt('cancelled'),  color: '#EF4444', icon: <Cancel />,         sub: 'Declined' },
        ].map((s) => (
          <Grid item xs={6} sm={4} md={2.4} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* ── Filters ── */}
      <Card elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid #E5E7EB', bgcolor: '#fff' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField placeholder="Search by name, mobile, property..."
              fullWidth size="small" value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#9CA3AF', fontSize: 18 }} /></InputAdornment>,
                endAdornment: searchKeyword && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchKeyword('')}>
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, fontSize: 13 }
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="date" fullWidth size="small" label="Date" value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: 2, fontSize: 13 } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Status" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              InputProps={{ sx: { borderRadius: 2, fontSize: 13 } }}>
              <MenuItem value="all">All Status</MenuItem>
              {['Scheduled','completed','cancelled','pending'].map(s => (
                <MenuItem key={s} value={s}><StatusBadge status={s} /></MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Property Type" value={propertyTypeFilter}
              onChange={(e) => setPropertyTypeFilter(e.target.value)}
              InputProps={{ sx: { borderRadius: 2, fontSize: 13 } }}>
              <MenuItem value="all">All Types</MenuItem>
              {['Apartment','Project','Property','Individual Villa'].map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            {hasFilters ? (
              <Button onClick={clearFilters} fullWidth variant="outlined" size="small" startIcon={<Clear />}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: 13,
                  borderColor: '#EF4444', color: '#EF4444',
                  '&:hover': { bgcolor: '#FEF2F2', borderColor: '#EF4444' } }}>
                Clear Filters
              </Button>
            ) : (
              <Typography sx={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No filters active</Typography>
            )}
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
            Showing <strong>{filteredVisits.length}</strong> of <strong>{siteVisits.length}</strong> visits
          </Typography>
          {hasFilters && (
            <Chip label="Filtered" size="small" color="primary" variant="outlined"
              sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 1 } }} />
          )}
        </Box>
      </Card>

      {/* ── Data Grid ── */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <Box sx={{ height: 650 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
              <CircularProgress sx={{ color: '#6366F1' }} />
              <Typography sx={{ color: '#6B7280', fontSize: 14 }}>Loading site visits...</Typography>
            </Box>
          ) : (
            <DataGrid
              rows={groupedRows}
              columns={columns}
              getRowId={(row) => row.id}
              pageSize={pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageSizeChange={(n) => setPageSize(n)}
              disableSelectionOnClick
              rowHeight={62}
              getRowClassName={(p) => p.row.isHeader ? 'date-header-row' : 'data-row'}
              isRowSelectable={(p) => !p.row.isHeader}
              sx={{
                border: 'none', fontFamily: 'inherit',
                // ── Date header rows styling ──
                '& .date-header-row': {
                  bgcolor: '#F0F4FF !important',
                  borderTop: '2px solid #C7D2FE !important',
                  borderBottom: '1px solid #C7D2FE !important',
                  cursor: 'default',
                  pointerEvents: 'none',
                },
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: '#F8FAFC', borderBottom: '2px solid #E5E7EB',
                  fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '.06em',
                },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .data-row': {
                  borderBottom: '1px solid #F3F4F6',
                  '&:hover': { bgcolor: '#F5F3FF' },
                },
                '& .MuiDataGrid-cell': { border: 'none', py: 1 },
                '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E5E7EB', bgcolor: '#F8FAFC' },
              }}
            />
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default SiteVisit;
